
import { Dealer } from "@/types/dealer";
import { Coordinates } from "../types";
import { 
  formatDealersData,
  sortDealersByPriority,
  sortComproOroByPriority
} from "../utils";
import { 
  getCachedRecommendedDealers,
  cacheRecommendedDealers,
  invalidateAllDealerCaches
} from "../cache";
import { fetchDataFromSources } from "./dataSources";
import { generateCacheKey } from "@/lib/recommended-dealers-cache/cacheConfig";

/**
 * Core implementation of recommended dealers data fetching with processing
 * CRITICAL: Preserves exact rating and buySpread values
 */
export const fetchDealersCore = async (
  selectedMetal: string,
  coordinates?: Coordinates | null,
  isRefresh: boolean = false,
  signal?: AbortSignal,
  limitCount?: number
): Promise<Dealer[]> => {
  try {
    // SSR-safe detection of Compro Oro page for specialized sorting
    const isComproOroPage = (): boolean => {
      // SSR: Always return false - server doesn't know the page context
      if (typeof window === 'undefined') {
        return false;
      }
      try {
        const path = window.location?.pathname;
        return typeof path === 'string' && path.startsWith('/compro-oro');
      } catch {
        return false;
      }
    };

    // SSR-safe extraction of sortOrder from URL params
    const sortOrder = (() => {
      // SSR: Use default sort order
      if (typeof window === 'undefined') {
        return 'priority';
      }
      try {
        if (isComproOroPage()) {
          const urlParams = new URLSearchParams(window.location?.search || '');
          return urlParams.get('sort') || 'priority';
        }
      } catch {
        // Silently fallback on any window access error
      }
      return 'priority';
    })();

    // UNIFICAZIONE CACHE: Usa chiavi standardizzate per coerenza
    const baseKey = generateCacheKey(selectedMetal);
    const coordinatesKey = coordinates ? generateCacheKey(selectedMetal, { 
      lat: coordinates.latitude, 
      lng: coordinates.longitude 
    }) : null;

    // Only invalidate cache if isRefresh is true
    if (isRefresh) {
      invalidateAllDealerCaches();
    } else {
      // STRATEGIA CACHE: coordinate-first per evitare che la cache base blocchi il re-fetch
      let cachedDealers: Dealer[] | null = null;
      if (coordinatesKey) {
        // Con coordinate, cerchiamo SOLO la cache con coordinate
        cachedDealers = getCachedRecommendedDealers(coordinatesKey);
      } else {
        // Senza coordinate, usiamo la cache base
        cachedDealers = getCachedRecommendedDealers(baseKey);
      }
      
      if (cachedDealers && cachedDealers.length > 0) {
        return cachedDealers;
      }
    }
    
    // Fetch fresh data from the sources - pass coordinates for distance sorting
    const { dealersData } = await fetchDataFromSources(selectedMetal, signal, coordinates, limitCount);
    
    if (!dealersData || dealersData.length === 0) {
      return [];
    }
    
    // Format the dealers data - PRESERVE EXACT VALUES
    let formattedDealers = formatDealersData(dealersData, selectedMetal);

    // Sort dealers by priority - use Compro Oro specialized sorting on Compro Oro pages
    let sortedDealers;
    if (isComproOroPage()) {
      sortedDealers = sortComproOroByPriority(formattedDealers, coordinates, sortOrder);
    } else {
      sortedDealers = sortDealersByPriority(formattedDealers, coordinates);
    }
    
    // CACHE: Salva nella chiave appropriata, NON sovrascrivere base con dati coordinate
    if (sortedDealers.length > 0) {
      if (coordinatesKey) {
        // Con coordinate: salva SOLO nella cache coordinate-specific
        cacheRecommendedDealers(coordinatesKey, sortedDealers);
      } else {
        // Senza coordinate: salva nella cache base
        cacheRecommendedDealers(baseKey, sortedDealers);
      }
    }
    
    return sortedDealers;
  } catch (error) {
    console.error('Error in fetchDealersCore:', error);
    return [];
  }
};
